/**
 * FormSharingCards — Accordion cards for sharing a form via hosted page link,
 * embed code, or overlay trigger.
 *
 * Shared across CreateFundraiserDrawer, EditFundraiserDrawer, CreateEventDrawer,
 * and EditEventDrawer. Accepts form type ('donate' | 'attend'), element ID,
 * and callbacks for copy/QR actions.
 */

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { copyToClipboard } from '@charitystack/shared';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Label } from '@/components/ui/label';
import OverlaySnippets from './OverlaySnippets';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { FEATURE } from '../../utils/entitlements';

type ShareCardKey = 'link' | 'embed' | 'overlay' | null;

interface FormSharingCardsProps {
  /** The published form/element ID */
  elementID: string;
  /** 'donate' for fundraisers, 'attend' for events */
  formType: 'donate' | 'attend';
  /** The embed entity type in the div snippet */
  embedEntity: string;
  /** Full hosted page URL (e.g. https://charitystack.com/donate/{id}) */
  hostedPageUrl: string;
  /** Merchant's website base URLs for overlay snippets */
  websiteUrl?: string;
  /** Form brand color for overlay button styling */
  color?: string;
  /** Callback to open QR code modal */
  onShowQR: () => void;
  /** Whether a QR code has been generated */
  hasQRCode?: boolean;
}

// ── Copy icon SVG (reused across cards) ─────────────────────────────────
const CopyIcon = () => (
  <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
);

// ── Chevron icon for expand/collapse ────────────────────────────────────
const Chevron = ({ open }: { open: boolean }) => (
  <svg
    className={`h-3 w-3 shrink-0 text-ink-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" d="M6 9l6 6 6-6" />
  </svg>
);

// ── Accordion body wrapper (CSS grid expand/collapse) ───────────────────
function AccordionBody({ open, children }: { open: boolean; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows 0.2s ease' }}>
      <div style={{ overflow: 'hidden' }}>
        <div className="rounded-b-lg border-x border-b border-edge bg-paper-light px-5 py-4">
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Select-all helper ───────────────────────────────────────────────────
function selectSnippetText(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const sel = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(el);
  sel?.removeAllRanges();
  sel?.addRange(range);
}

export default function FormSharingCards({
  elementID,
  formType,
  embedEntity,
  hostedPageUrl,
  websiteUrl,
  color,
  onShowQR,
  hasQRCode = false,
}: FormSharingCardsProps) {
  const [activeCard, setActiveCard] = useState<ShareCardKey>('link');

  const toggle = useCallback((key: ShareCardKey) => {
    setActiveCard((prev) => (prev === key ? null : key));
  }, []);

  const handleCopyUrl = useCallback(async () => {
    const success = await copyToClipboard(hostedPageUrl);
    toast[success ? 'success' : 'error'](success ? 'URL copied!' : 'Failed to copy');
  }, [hostedPageUrl]);

  const embedSnippet = `<div data-entity="${embedEntity}" data-source="CharityStack" id="${elementID}"></div>`;

  const handleCopyEmbed = useCallback(async () => {
    const success = await copyToClipboard(embedSnippet);
    toast[success ? 'success' : 'error'](success ? 'Embed code copied!' : 'Failed to copy');
  }, [embedSnippet]);

  const description = formType === 'donate' ? 'donation' : 'event';
  const hasOrgWebsite = Boolean(websiteUrl?.trim());

  // Overlay (popup) is a Base+ growth feature (overlay_popup). Hide the Overlay
  // card for orgs not entitled — embed + hosted-link sharing stay available to
  // all tiers. Fail-open when resolution degraded (corrupt billingDetails).
  const { can: canEntitle, degraded: entDegraded } = useEntitlements();
  const canOverlay = entDegraded || canEntitle(FEATURE.OVERLAY_POPUP);
  const cardShell = 'overflow-hidden rounded-lg bg-paper-light';

  return (
    <div className="rounded-md bg-transparent">
      <Label className="mb-2 block text-base font-medium leading-5 text-ink">
        Share Your Form
      </Label>
      <div className="space-y-4">

        {/* ── Card 1: Share a Link ── */}
        <div className={cardShell}>
          <button type="button" className="flex w-full items-start gap-2 rounded-t-lg border border-edge bg-white px-4 py-2.5 text-left transition-colors hover:bg-paper-muted focus:outline-none focus:ring-0" onClick={() => toggle('link')}>
            <svg className="h-[15px] w-[15px] shrink-0 text-ink-muted" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
            </svg>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-ink">Share a Link</span>
              <p className="text-[11px] text-ink-secondary mt-px">Send people to your hosted {description} page</p>
            </div>
            <Chevron open={activeCard === 'link'} />
          </button>
          <AccordionBody open={activeCard === 'link'}>
            <div className="flex gap-1">
              <div
                role="textbox"
                aria-readonly="true"
                tabIndex={0}
                className="flex min-h-[2.25rem] flex-1 items-center rounded-md border border-edge bg-paper-inset px-2.5 py-1.5 text-[11px] font-mono text-ink-secondary break-all select-all cursor-text"
                onClick={(e) => { e.stopPropagation(); selectSnippetText(`url-snippet-${elementID}`); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectSnippetText(`url-snippet-${elementID}`);
                  }
                }}
              >
                <span id={`url-snippet-${elementID}`}>{hostedPageUrl}</span>
              </div>
              <button
                type="button"
                className="flex items-center gap-1 rounded-md border border-edge px-2 text-[11px] font-medium text-ink-muted hover:text-ink-secondary hover:bg-paper-inset transition-colors"
                onClick={(e) => { e.stopPropagation(); handleCopyUrl(); }}
                title="Copy URL"
              >
                <CopyIcon />
                Copy
              </button>
              <button
                type="button"
                className="flex items-center justify-center rounded-md border border-edge px-1.5 text-ink-muted hover:text-ink-secondary hover:bg-paper-inset transition-colors"
                onClick={(e) => { e.stopPropagation(); onShowQR(); }}
                title={hasQRCode ? 'QR Code Generated - Click to View' : 'Show QR Code'}
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24" strokeLinecap="round">
                  <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" />
                  <path d="M14 14h.01M14 17h3M17 14v3M20 14h.01M14 20h.01M17 20h3M20 17v3" />
                </svg>
              </button>
            </div>
          </AccordionBody>
        </div>

        {/* ── Card 2: Embed Code ── */}
        <div className={cardShell}>
          <button type="button" className="flex w-full items-start gap-2 rounded-t-lg border border-edge bg-white px-4 py-2.5 text-left transition-colors hover:bg-paper-muted focus:outline-none focus:ring-0" onClick={() => toggle('embed')}>
            <svg className="h-[15px] w-[15px] shrink-0 text-ink-muted" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
            </svg>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-ink">Embed Code</span>
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InformationCircleIcon className="h-3.5 w-3.5 cursor-help text-ink-muted hover:text-ink-secondary" onClick={(e) => e.stopPropagation()} />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p>Requires the CharityStack script in your site&apos;s &lt;head&gt;. Find it in Settings → Embed.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-[11px] text-ink-secondary mt-px">Place the form directly on a page of your website</p>
            </div>
            <Chevron open={activeCard === 'embed'} />
          </button>
          <AccordionBody open={activeCard === 'embed'}>
            <div className="flex gap-1">
              <div
                role="textbox"
                aria-readonly="true"
                tabIndex={0}
                className="flex min-h-[2.25rem] flex-1 items-center rounded-md border border-edge bg-paper-inset px-2.5 py-1.5 text-[11px] font-mono text-ink-secondary break-all select-all cursor-text"
                onClick={(e) => { e.stopPropagation(); selectSnippetText(`embed-snippet-${elementID}`); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectSnippetText(`embed-snippet-${elementID}`);
                  }
                }}
              >
                <span id={`embed-snippet-${elementID}`}>{embedSnippet}</span>
              </div>
              <button
                type="button"
                className="flex items-center gap-1 rounded-md border border-edge px-2 text-[11px] font-medium text-ink-muted hover:text-ink-secondary hover:bg-paper-inset transition-colors"
                onClick={(e) => { e.stopPropagation(); handleCopyEmbed(); }}
                title="Copy Embed Code"
              >
                <CopyIcon />
                Copy
              </button>
            </div>
          </AccordionBody>
        </div>

        {/* ── Card 3: Overlay Trigger (Base+ — overlay_popup) ── */}
        {canOverlay && (
        <div className={cardShell}>
          <button type="button" className="flex w-full items-start gap-2 rounded-t-lg border border-edge bg-white px-4 py-2.5 text-left transition-colors hover:bg-paper-muted focus:outline-none focus:ring-0" onClick={() => toggle('overlay')}>
            <svg className="h-[15px] w-[15px] shrink-0 text-ink-muted" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" /><rect x="6" y="8" width="12" height="9" rx="1.5" fill="rgba(107,142,107,0.1)" /><line x1="9" y1="12" x2="15" y2="12" />
            </svg>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium text-ink">Overlay Trigger</span>
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InformationCircleIcon className="h-3.5 w-3.5 cursor-help text-ink-muted hover:text-ink-secondary" onClick={(e) => e.stopPropagation()} />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-sm text-left">
                      <p className="mb-2 text-xs leading-relaxed">
                        Add a button or link to your website that opens this form as a popup overlay.
                        Your site must have the CharityStack script installed for these to work.
                      </p>
                      <p className="text-xs leading-relaxed text-ink-muted">
                        {hasOrgWebsite ? (
                          <>
                            Note: CharityStack verifies overlay traffic using the website URL in
                            Settings → Organization.
                          </>
                        ) : (
                          <>
                            Note: Your overlay elements are not protected by domain verification. Add
                            your website URL in Settings → Organization to restrict overlays to your
                            domain.
                          </>
                        )}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-[11px] text-ink-secondary mt-px">Use the overlay link or add a {description === 'donation' ? 'donate' : 'register'} button to your website to trigger the overlay</p>
            </div>
            <Chevron open={activeCard === 'overlay'} />
          </button>
          <AccordionBody open={activeCard === 'overlay'}>
            <OverlaySnippets
              elementID={elementID}
              type={formType}
              websiteUrl={websiteUrl}
              color={color}
              layout="sharing"
              showHeader={false}
            />
          </AccordionBody>
        </div>
        )}

      </div>
    </div>
  );
}
